import { createEntityAdapter, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { CmtsSerializableError } from "./thunkExtra.js";

export type CmtsAdminUserEntity = { readonly id: string; readonly loginId?: string | undefined; readonly displayName?: string | undefined; readonly email?: string | undefined; readonly active?: boolean | undefined };
export type CmtsAdminRoleEntity = { readonly id: string; readonly code: string; readonly name: string };
export type CmtsAdminPermissionEntity = { readonly id: string; readonly code: string; readonly name: string };

const userAdapter = createEntityAdapter<CmtsAdminUserEntity>();
const roleAdapter = createEntityAdapter<CmtsAdminRoleEntity>();
const permissionAdapter = createEntityAdapter<CmtsAdminPermissionEntity>();

export type CmtsAdminState = {
  readonly users: ReturnType<typeof userAdapter.getInitialState>;
  readonly roles: ReturnType<typeof roleAdapter.getInitialState>;
  readonly permissions: ReturnType<typeof permissionAdapter.getInitialState>;
  readonly selectedAdminEntity?: string | undefined;
  readonly selectedIds: readonly string[];
  readonly filterText: string;
  readonly actionLoading: boolean;
  readonly error?: CmtsSerializableError | undefined;
  readonly lastAssignmentAtUtc?: string | undefined;
};

const initialState: CmtsAdminState = {
  users: userAdapter.getInitialState(),
  roles: roleAdapter.getInitialState(),
  permissions: permissionAdapter.getInitialState(),
  selectedIds: [],
  filterText: "",
  actionLoading: false,
};

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    adminEntitySelected(state, action: PayloadAction<string | undefined>) {
      state.selectedAdminEntity = action.payload;
      state.selectedIds = [];
    },
    adminUsersLoaded(state, action: PayloadAction<readonly CmtsAdminUserEntity[]>) {
      userAdapter.setAll(state.users, action.payload);
    },
    adminRolesLoaded(state, action: PayloadAction<readonly CmtsAdminRoleEntity[]>) {
      roleAdapter.setAll(state.roles, action.payload);
    },
    adminPermissionsLoaded(state, action: PayloadAction<readonly CmtsAdminPermissionEntity[]>) {
      permissionAdapter.setAll(state.permissions, action.payload);
    },
    adminSelectionChanged(state, action: PayloadAction<readonly string[]>) {
      state.selectedIds = [...action.payload];
    },
    adminFilterChanged(state, action: PayloadAction<string>) {
      state.filterText = action.payload;
    },
    adminActionStarted(state) {
      state.actionLoading = true;
      state.error = undefined;
    },
    adminActionCompleted(state) {
      state.actionLoading = false;
      state.lastAssignmentAtUtc = new Date().toISOString();
    },
    adminErrorRecorded(state, action: PayloadAction<CmtsSerializableError>) {
      state.actionLoading = false;
      state.error = action.payload;
    },
    adminCleared(state) {
      userAdapter.removeAll(state.users);
      roleAdapter.removeAll(state.roles);
      permissionAdapter.removeAll(state.permissions);
      state.selectedAdminEntity = undefined;
      state.selectedIds = [];
      state.filterText = "";
      state.actionLoading = false;
      state.error = undefined;
      state.lastAssignmentAtUtc = undefined;
    },
  },
});

export const adminUserEntityAdapter = userAdapter;
export const adminRoleEntityAdapter = roleAdapter;
export const adminPermissionEntityAdapter = permissionAdapter;
export const adminActions = adminSlice.actions;
export const adminReducer = adminSlice.reducer;
